import { CheckCircle, XCircle, PauseCircle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

type UserStatus = 'active' | 'suspended' | 'deleted';

/**
 * Fallback for any status value not in `statusConfig` — including `null`
 * (some core.users_core rows have a NULL status) and future/unknown values.
 * Prior to this guard `statusConfig[status].icon` threw on an unmapped
 * status, and with no error boundary that blanked the entire admin SPA
 * (repro: /users?userType=real&pageSize=100 loaded 2 NULL-status users).
 * Renders a neutral "Unknown" pill instead of crashing.
 */
const UNKNOWN_STATUS_CONFIG = {
  icon: HelpCircle,
  label: 'Unknown',
  className: 'bg-muted text-muted-foreground border-border',
  iconClassName: 'text-muted-foreground',
};

const statusConfig = {
  active: {
    icon: CheckCircle,
    label: 'Active',
    className: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
    iconClassName: 'text-green-600 dark:text-green-400',
  },
  suspended: {
    icon: PauseCircle,
    label: 'Suspended',
    className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800',
    iconClassName: 'text-amber-600 dark:text-amber-400',
  },
  deleted: {
    icon: XCircle,
    label: 'Deleted',
    className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800',
    iconClassName: 'text-red-600 dark:text-red-400',
  },
};

interface StatusBadgeProps {
  // Widened from `UserStatus` to accept null/unknown values: the DB can hold
  // a NULL status, and a hard `UserStatus`-only type let callers pass values
  // that crashed at runtime. The component now degrades gracefully.
  status: UserStatus | string | null | undefined;
  size?: 'sm' | 'md';
  showIcon?: boolean;
}

export function StatusBadge({ status, size = 'md', showIcon = true }: StatusBadgeProps) {
  const config =
    (status != null && (statusConfig as Record<string, typeof UNKNOWN_STATUS_CONFIG>)[status]) ||
    UNKNOWN_STATUS_CONFIG;
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses[size],
        config.className
      )}
    >
      {showIcon && <Icon className={cn(iconSizes[size], config.iconClassName)} />}
      {config.label}
    </span>
  );
}
